import React, { FC, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { Form, FormItem, Input } from 'formik-antd';
import { object } from 'yup';
import { FormikOnSubmit } from 'lib/formik.utils';
import {
  requiredEmailValidationSchema,
  firstNameValidationSchema,
  lastNameValidationSchema,
  phoneNumberValidationSchema,
  phoneNumberPrefixCodeValidation,
  jobTitleValidationSchema,
} from 'common/config/validationSchema';
import { defaultFormLayout } from 'common/config/formLayout';
import { MedicalOperatorFormModel } from 'models/medicalOperator';
import { AdministrationGuard } from 'common/components/AdministrationGuard';
import { useRole } from 'common/hooks/useRole';
import { SaveButton } from 'common/components/SaveButton';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { SubpartnersRegisterSelect } from 'common/components/SubpartnersSelect';
import { Checkbox } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useQuery } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { isNil } from 'ramda';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useEnsureContext } from 'common/hooks';

export interface BusinessPartnerRegisterMedicalOperatorFormProps {
  onSubmit: FormikOnSubmit<MedicalOperatorFormModel>;
  loading: boolean;
  initialValues: MedicalOperatorFormModel;
  checkedDenyAccessToApps: boolean;
  setCheckedDenyAccessToApps: React.Dispatch<React.SetStateAction<boolean>>;
  checkedDenyAccessToPatients: boolean;
  setCheckedDenyAccessToPatients: React.Dispatch<React.SetStateAction<boolean>>;
  checkedAccessToHigoProOne: boolean;
  setCheckedAccessToHigoProOne: React.Dispatch<React.SetStateAction<boolean>>;
  medicalOperatorId: number;
  setShouldPutPermissions: React.Dispatch<React.SetStateAction<boolean>>;
}

export const BusinessPartnerMedicalOperatorEditForm: FC<BusinessPartnerRegisterMedicalOperatorFormProps> = ({
  onSubmit,
  loading = false,
  initialValues,
  checkedDenyAccessToApps,
  setCheckedDenyAccessToApps,
  checkedDenyAccessToPatients,
  setCheckedDenyAccessToPatients,
  checkedAccessToHigoProOne,
  setCheckedAccessToHigoProOne,
  medicalOperatorId,
  setShouldPutPermissions,
}) => {
  const { isAdministrationRole } = useRole();
  const intlMessage = useFormatMessage();
  const [checkedAccessApps, setCheckedAccessApps] = useState(true);
  const [checkedAccessPatients, setCheckedAccessPatients] = useState(true);
  const [checkedAccessHigoPro, setCheckedAccessHigoPro] = useState(true);

  const [permissionsSet, setpermissionsSet] = useState(false);
  const { isMedicalOperator } = useRole();

  const validationSchema = object().shape(
    {
      firstName: firstNameValidationSchema(intlMessage),
      lastName: lastNameValidationSchema(intlMessage),
      email: requiredEmailValidationSchema(intlMessage),
      phoneNumber: phoneNumberValidationSchema(intlMessage),
      prefixCode: phoneNumberPrefixCodeValidation(intlMessage),
      jobTitle: jobTitleValidationSchema(intlMessage),
    },
    [
      ['prefixCode', 'phoneNumber'],
      ['phoneNumber', 'prefixCode'],
    ]
  );

  const { data, isSuccess } = useQuery(
    [QueryKey.BusinessPartnerMedicalOperator],
    () => BusinessPartnersAPI.fetchUserPermissions(medicalOperatorId),
    {
      enabled: !isMedicalOperator,
    }
  );

  useEffect(() => {
    const portalsPermission =
      isSuccess &&
      data.items.find(
        (item: { permission: string; enabled: boolean }) =>
          item.permission === 'DOCTOR_PORTAL_ACCESS'
      );
    const portalAccessEnabled = portalsPermission.enabled;
    setCheckedAccessApps(!portalAccessEnabled);
    setCheckedDenyAccessToApps(!portalAccessEnabled);

    const patientsPermission =
      isSuccess &&
      data.items.find(
        (item: { permission: string; enabled: boolean }) =>
          item.permission === 'PATIENT'
      );
    const patientsPermissionEdit = patientsPermission.create;
    setCheckedAccessPatients(!patientsPermissionEdit);
    setCheckedDenyAccessToPatients(!patientsPermissionEdit);

    const HigoProPermission =
      isSuccess &&
      data.items.find(
        (item: { permission: string; enabled: boolean }) =>
          item.permission === 'PATIENT_ASSIGNMENT'
      );
    const higoProPermissionEdit = HigoProPermission.create;
    setCheckedAccessHigoPro(!higoProPermissionEdit);
    setCheckedAccessToHigoProOne(!higoProPermissionEdit);
  }, [
    isSuccess,
    data,
    setCheckedDenyAccessToApps,
    setCheckedDenyAccessToPatients,
    setCheckedAccessToHigoProOne,
  ]);

  //seTimeout used as a temporary solution waiting for the dedicated permissions screen in the app (set to 500 as we have to wait for setCheked() to set to the proper permission from backend)
  setTimeout(() => {
    setpermissionsSet(true);
  }, 500);

  const bpContext = useEnsureContext(BusinessPartnerContext);

  const shouldHidePartnerDropdown =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;

  const medicalOperatorEditInitialValues = {
    email: initialValues?.email,
    firstName: initialValues.firstName,
    lastName: initialValues.lastName,
    phoneNumber: initialValues.phoneNumber,
    prefixCode: initialValues.prefixCode,
    jobTitle: initialValues.jobTitle,
    partner: initialValues.partner,
    denyAccessToApps: !checkedAccessApps,
    denyAccessToPatients: !checkedAccessPatients,
    denycheckedAccessHigoPro: !checkedAccessHigoPro,
  };

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      initialValues={medicalOperatorEditInitialValues}
      onSubmit={onSubmit}
    >
      <fieldset disabled={!isAdministrationRole}>
        <Form {...defaultFormLayout}>
          <FormItem
            label={intlMessage({ id: 'person.firstName' })}
            name="firstName"
            required
          >
            <Input name="firstName" disabled={isMedicalOperator} />
          </FormItem>
          <FormItem
            label={intlMessage({ id: 'person.lastName' })}
            name="lastName"
            required
          >
            <Input name="lastName" disabled={isMedicalOperator} />
          </FormItem>
          <FormItem label={intlMessage({ id: 'email' })} name="email" required>
            <Input readOnly disabled name="email" />
          </FormItem>{' '}
          <FormItem
            name="phone"
            label={intlMessage({ id: 'person.phoneNumber' })}
            style={{ display: 'flex', flexDirection: 'row', width: '100%' }}
          >
            <FormItem
              name="phoneNumber"
              style={{ marginBottom: '0', display: 'flex', width: '100%' }}
            >
              <FormItem
                name="prefixCode"
                style={{
                  marginBottom: '0',
                  width: 95,
                  display: 'inline-block',
                }}
              >
                <Input
                  name="prefixCode"
                  style={{ width: 95 }}
                  addonBefore={'+'}
                  disabled={isMedicalOperator}
                />
              </FormItem>
              <Input
                style={{ width: 240, marginLeft: 10 }}
                name="phoneNumber"
                disabled={isMedicalOperator}
              />
            </FormItem>
          </FormItem>
          <FormItem
            label={intlMessage({ id: 'person.jobTitle' })}
            name="jobTitle"
          >
            <Input name="jobTitle" disabled={isMedicalOperator} />
          </FormItem>
          {!shouldHidePartnerDropdown && (
            <FormItem label={intlMessage({ id: 'partner' })} name="partner">
              <SubpartnersRegisterSelect
                defaultValue={
                  initialValues.partnerDetails
                    ? initialValues.partnerDetails.name
                    : ''
                }
                disabled
                name="partner-edit"
              />
            </FormItem>
          )}
          {!isMedicalOperator && (
            <>
              <FormItem
                name="denyAccess"
                style={{ marginLeft: '200px', width: '100%' }}
              >
                {isSuccess && permissionsSet === true && (
                  <Checkbox
                    key="denyAccessToApps"
                    name={'denyAccessToApps'}
                    defaultChecked={checkedAccessApps}
                    onChange={() => {
                      setCheckedDenyAccessToApps &&
                        setCheckedDenyAccessToApps(!checkedDenyAccessToApps);
                      setShouldPutPermissions(true);
                    }}
                  />
                )}
                <span style={{ marginLeft: '7px' }}>
                  {' '}
                  <FormattedMessage id="medicalOperators.denyAccessToApps" />
                </span>
              </FormItem>

              <FormItem
                name="denyAccessToPatients"
                style={{ marginLeft: '200px', width: '100%' }}
              >
                {isSuccess && permissionsSet === true && (
                  <Checkbox
                    key="denyAccessToPatients"
                    name={'denyAccessToPatients'}
                    defaultChecked={checkedAccessPatients}
                    onChange={() => {
                      setCheckedDenyAccessToPatients &&
                        setCheckedDenyAccessToPatients(
                          !checkedDenyAccessToPatients
                        );
                      setShouldPutPermissions(true);
                    }}
                  />
                )}
                <span style={{ marginLeft: '7px' }}>
                  {' '}
                  {/* <FormattedMessage id="medicalOperators.denyAccessToPatiens" />  - add this key to the translation file*/}
                  Deny access to creating and editing patients
                </span>
              </FormItem>

              <FormItem
                name="accessToHigoProOne"
                style={{ marginLeft: '200px', width: '100%' }}
              >
                {isSuccess && permissionsSet === true && (
                  <Checkbox
                    key="accessToHigoProOne"
                    name={'accessToHigoProOne'}
                    defaultChecked={checkedAccessHigoPro}
                    onChange={() => {
                      setCheckedAccessToHigoProOne &&
                        setCheckedAccessToHigoProOne(
                          !checkedAccessToHigoProOne
                        );
                      setShouldPutPermissions(true);
                    }}
                  />
                )}
                <span style={{ marginLeft: '7px' }}>
                  {' '}
                  {/* <FormattedMessage id="medicalOperators.denyAccessToPatiens" />  - add this key to the translation file*/}
                  Higo PRO One
                </span>
              </FormItem>
            </>
          )}
          <AdministrationGuard>
            {!isMedicalOperator && <SaveButton loading={loading} />}
          </AdministrationGuard>
        </Form>
      </fieldset>
    </Formik>
  );
};
